import { Tooltip, Link, Checkbox, Chip } from "@mui/material";
import { round } from "lodash";
import {
  multiplierOptions,
  getFileNameFromUrl,
  multiplierMappings,
  prettyNumericRound,
  getFormattedNumber,
} from "../../utils";
import { getTierLevelForQty } from "../../utils/pogCalculation";
import NumericLabel from "../../components/NumericLabel";
import { NOTE_TYPES } from "../../store/notesSlice";
import CustomEditComponent from "../DiscountAndPlanogram/DiscountTable/CustomEditComponent";
import AutoStepInput from "../../components/AutoStepInput";
import config from "../../config";

const TierColorMappings = {
  1: "#0288d1bd", // T1-Blue
  2: "#8bc34ad1", // T2-Green
  3: "#ffc107ab", // T3-Yellow
  4: "#ff5722b8", // T4-Red
};

const getPlanogramItemColumns = (
  processCallback = () => {},
  plannerNotes = [],
  retailerNotes = [],
  restrictEdit
) => [
  {
    field: "fl_dept_name",
    headerName: "Department",
    width: 160,
    sortable: false,
  },
  {
    field: "fl_class_name",
    headerName: "Category",
    width: 160,
  },
  {
    field: "fl_sub_name",
    headerName: "Planogram",
    width: 160,
  },
  {
    field: "planogram",
    headerName: "Planogram #",
    width: 130,
    renderCell: (params) => (
      <Tooltip title={params.value ? getFileNameFromUrl(params.value) : ""}>
        <Link
          href={config.PogFileBasePath.concat(params.value)}
          target="_blank"
          rel="noreferrer noopener"
        >
          {getFileNameFromUrl(params.value)}
        </Link>
      </Tooltip>
    ),
  },
  {
    field: "core",
    headerName: "Core",
    width: 60,
    sortable: false,
    renderCell: (params) =>
      !params.value || params.value === "N" ? "No" : "Yes",
  },
  {
    field: "breakPackType",
    headerName: "Pack Type",
    width: 90,
    renderCell: (params) => {
      return params?.value ? params?.value : "-";
    },
  },
  {
    field: "pogqty",
    headerName: "Qty",
    width: 80,
    renderCell: (params) => (
      <NumericLabel isSeparator value={round(params?.value || 0, 2)} />
    ),
  },
  {
    field: "pogcost",
    headerName: "Cost",
    width: 90,
    renderCell: (params) => {
      const value = prettyNumericRound(params.value || 0);
      return `$${getFormattedNumber(value)}`;
    },
  },
  {
    field: "Item_Nbr",
    headerName: "SKU",
    width: 80,
  },
  {
    field: "short_description",
    headerName: "Item Description",
    width: 140,
    renderCell: (params) => (
      <Tooltip title={params.value}>
        <span>{params.value}</span>
      </Tooltip>
    ),
  },
  {
    field: "vendor_name",
    headerName: "Vendor",
    width: 100,
    renderCell: (params) => (
      <Tooltip title={params.value}>
        <span>{params.value}</span>
      </Tooltip>
    ),
  },
  {
    field: "missingskus",
    headerName: "Missing",
    width: 110,
    sortable: false,
    renderCell: (params) => (
      <NumericLabel isSeparator value={round(params?.value || 0, 2)} />
    ),
  },
  {
    field: "multiplier",
    headerName: "Multiplier",
    width: 85,
    sortable: false,
    renderCell: (params) => {
      return params?.value ? multiplierMappings[params.value] : "-";
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="multiplier"
        default=""
        options={multiplierOptions}
      />
    ),
    editable: true,
  },
  {
    field: "tier",
    headerName: "Tier",
    width: 60,
    sortable: false,
    renderCell: (params) => {
      const currentTier = getTierLevelForQty(params.row);
      return !!currentTier ? (
        <Chip
          label={`T${currentTier}`}
          size="small"
          sx={{
            backgroundColor: TierColorMappings[currentTier],
            color: "#fff",
          }}
        />
      ) : (
        "-"
      );
    },
  },
  {
    field: "adjqty",
    headerName: "Adj. Qty",
    width: 80,
    renderCell: (params) => (
      <NumericLabel isSeparator value={round(params?.value || 0, 2)} />
    ),
    renderEditCell: (params) => {
      const packValues = params?.row?.breakPackType.split("F");
      const isFPack = packValues.length === 2;
      const stepper = isFPack ? packValues[1] : 0;
      return isFPack ? (
        <AutoStepInput
          {...params}
          getProcessedRow={processCallback}
          fieldType="adjQty"
          stepper={parseInt(stepper)}
        />
      ) : (
        <CustomEditComponent
          {...params}
          getProcessedRow={processCallback}
          fieldType="adjQty"
        />
      );
    },
    editable: true,
  },
  {
    field: "adjcost",
    headerName: "Adj. Cost",
    width: 110,
    renderCell: (params) => {
      const value = prettyNumericRound(params.value || 0);
      return `$${getFormattedNumber(value)}`;
    },
  },
  {
    field: "discMultiplier",
    headerName: "Discount Multiplier",
    width: 85,
    sortable: false,
    renderCell: (params) => {
      return params?.value ? multiplierMappings[params.value] : "-";
    },
  },
  {
    field: "discountPercentage",
    headerName: "Discount",
    width: 100,
    sortable: true,
    renderCell: (params) => {
      return `${round(params.value || 0, 2)}%`;
    },
  },
  {
    field: "totalCost",
    headerName: "Total Cost",
    width: 110,
    renderCell: (params) => {
      const value = prettyNumericRound(params.value || 0);
      return `$${getFormattedNumber(value)}`;
    },
  },
  {
    field: "PlannerAction",
    headerName: "Status",
    type: "boolean",
    width: 70,
    sortable: false,
    renderCell: (params) => {
      return (
        <Checkbox
          readOnly
          checked={Boolean(params.value)}
          disabled={!params.row.plannernotesid}
          sx={{ width: "auto", padding: "0rem" }}
        />
      );
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="checkbox"
        disabled={!params.row.plannernotesid}
      />
    ),
    editable: true,
  },
  {
    field: "plannernotesid",
    headerName: "Planner Notes",
    width: 200,
    sortable: false,
    renderCell: (params) => {
      let currentValue = plannerNotes.find((note) => note?.id === params.value);
      currentValue = !!currentValue ? currentValue?.Notes : "";
      return (
        <Tooltip title={currentValue ? currentValue : ""}>
          <span className="truncated-notes">{currentValue}</span>
        </Tooltip>
      );
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="autoComplete"
        noteType={NOTE_TYPES[0]}
        options={plannerNotes}
        restrictEdit={restrictEdit}
      />
    ),
    editable: true,
  },
  {
    field: "RetailerAction",
    headerName: "Status",
    type: "boolean",
    width: 70,
    sortable: false,
    renderCell: (params) => {
      return (
        <Checkbox
          readOnly
          checked={Boolean(params.value)}
          disabled={!params.row.retailernotesid}
          sx={{ width: "auto", padding: "0rem" }}
        />
      );
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="checkbox"
        disabled={!params.row.retailernotesid}
      />
    ),
    editable: true,
  },
  {
    field: "retailernotesid",
    headerName: "Retailer Notes",
    width: 200,
    sortable: false,
    renderCell: (params) => {
      let currentValue = retailerNotes.find(
        (note) => note?.id === params.value
      );
      currentValue = !!currentValue ? currentValue?.Notes : "";
      return (
        <Tooltip title={currentValue ? currentValue : ""}>
          <span className="truncated-notes">{currentValue}</span>
        </Tooltip>
      );
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="autoComplete"
        options={retailerNotes}
        noteType={NOTE_TYPES[1]}
        restrictEdit={restrictEdit}
      />
    ),
    editable: true,
  },
];

export const POG_ITEM_EDITABLE_FIELDS = getPlanogramItemColumns()
  .map((c) => c.editable && c.field)
  .filter((i) => !!i);

export default getPlanogramItemColumns;
